import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * The two readers for dates arriving from the API.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE ARE TWO, AND WHY THE WIRE LOOKS THE WAY IT DOES
 * ---------------------------------------------------------------------------
 * The backend stores two different kinds of value in `timestamp` columns:
 *
 *   a CALENDAR DATE  — `applied_from`, `end_date`, `birth_date`,
 *                      `current_date`, `start_date`, `meeting_date`…
 *                      A day on a calendar. It has no timezone. The column
 *                      holds the Buenos Aires wall clock, and the backend
 *                      models it with the `CalendarDate` class.
 *
 *   an INSTANT       — `its`, `uts`, `dts`, `executed_at`, `answer_date`…
 *                      A real moment. It genuinely has a timezone, and it
 *                      SHOULD render differently for a viewer in Madrid.
 *
 * Both arrive through the same GraphQL `Date` scalar, which serialises with
 * `toISOString()`. So both look like `"2026-10-01T00:00:00.000Z"`, and the
 * wire cannot tell you which kind you are holding. For an instant that `Z` is
 * the truth. For a calendar date it is a polite fiction: the UTC fields carry
 * the Buenos Aires wall clock, exactly as they do in the database and in the
 * backend's `CalendarDate`. One contract, three tiers.
 *
 * Hence: `readCalendarDate` reads the UTC fields and is identical in every
 * browser; `readInstant` resolves in the viewer's zone, which is what an
 * instant means. Calling the wrong one is the single most common date bug in
 * this app — it shows a Buenos Aires viewer the previous day for every
 * calendar date, because Argentina is behind UTC.
 *
 * ---------------------------------------------------------------------------
 * A THIRD JOB: SEEDING A DATE PICKER
 * ---------------------------------------------------------------------------
 * Putting a stored calendar date INTO an Ant Design DatePicker is not the same
 * job as reading one. The picker hands its value back through `toIsoDate`,
 * which reads LOCAL fields, so it must be seeded with a LOCAL-midnight dayjs.
 * `Tools.parseDateOnly` does that. `readCalendarDate` returns a UTC-mode
 * value, which used to corrupt the round-trip — while `toIsoDate` read local
 * fields it lost a day for any viewer behind UTC, rewriting birth_date and
 * date_from when a form was opened and saved untouched. `toIsoDate` now
 * formats a Dayjs in its own mode, so that leg is safe either way; what is
 * still unverified is how Ant Design itself behaves with a UTC-mode value,
 * which is why the 46 existing sites stay on `parseDateOnly`. Pinned by the
 * "seeding a date picker" tests.
 *
 * So: `readCalendarDate` to READ, `Tools.parseDateOnly` to SEED, `readInstant`
 * for real moments. The 46 existing `Tools.formatDate` / `parseDateOnly` call
 * sites are already correct and were deliberately left alone.
 *
 * ---------------------------------------------------------------------------
 * THIS IS A WORKAROUND. TWO THINGS WOULD MAKE IT UNNECESSARY.
 * ---------------------------------------------------------------------------
 * 1. SPLIT THE WIRE FORMAT. A calendar date should not be sent as an instant
 *    at all. If the scalar emitted a zoneless value — `"2026-10-01"`, or
 *    `"2026-10-01T23:00:00"` with no `Z` once a field carries a time — then
 *    the plain reader would be correct in every browser and there would be
 *    nothing to get wrong. We measured it: a zoneless ISO string read with
 *    the DEFAULT reader gives the same day and the same `HH:mm` in Buenos
 *    Aires, Madrid and Tokyo. It was not done because it changes the wire
 *    format for ~136 fields and needs a coordinated deployment, and because
 *    ~41 call sites already read calendar dates correctly with `.utc()`.
 *
 * 2. GENERATE THE TYPES. Queries here are typed by hand (`useQuery<T>`), so
 *    nothing tells a developer that `applied_from` is a calendar date and
 *    `its` is an instant — the knowledge lives in this comment and in which
 *    function you call. With GraphQL code generation and a split scalar, the
 *    two kinds would be different TypeScript types and picking the wrong
 *    reader would not compile. That is the real fix; this module is what
 *    makes the current shape survivable until then.
 * ---------------------------------------------------------------------------
 */

type ApiDate = string | Date | null | undefined;

const toIso = (value: Exclude<ApiDate, null | undefined>): string =>
  value instanceof Date ? value.toISOString() : String(value);

/**
 * Reads a CALENDAR DATE — a day on a calendar, with no timezone.
 *
 * Identical in every browser. Use for `applied_from`, `applied_until`,
 * `start_date`, `end_date`, `birth_date`, `current_date`, `meeting_date`,
 * `review_date`, `date_from`, `date_to`, `submitted_at` and anything else the
 * backend models with `CalendarDate`.
 */
export const readCalendarDate = (value: Exclude<ApiDate, null | undefined>): Dayjs =>
  dayjs.utc(toIso(value));

/**
 * Reads an INSTANT — a real moment, rendered in the viewer's timezone.
 *
 * Use for `its`, `uts`, `dts`, `executed_at`, `answer_date`, `last_update`,
 * `lastAccess` and the survey campaign start/end dates.
 */
export const readInstant = (value: Exclude<ApiDate, null | undefined>): Dayjs =>
  dayjs(toIso(value));

/**
 * Renders an INSTANT for display, in the viewer's timezone.
 *
 * The counterpart to `formatCalendarDate`. Use for `its`, `uts`,
 * `executed_at`, `answer_date` and the survey campaign dates — anything that
 * is a real moment. Feeding one of those to `formatCalendarDate` (or to
 * `Tools.formatMonthYear`, which is the same kind of reader) reports the UTC
 * day, so for a viewer behind UTC a request created at 23:00 on the 31st is
 * reported as the following month.
 */
export const formatInstant = (value: ApiDate, format: string): string => {
  if (!value) return '';
  const parsed = readInstant(value);
  return parsed.isValid() ? parsed.format(format) : '';
};

/**
 * Renders a calendar date for display. Never consults a timezone.
 *
 * `withTime` appends `HH:mm`, read as the Buenos Aires wall clock — which is
 * what makes this safe the day a date-only field starts carrying a time.
 */
export const formatCalendarDate = (
  value: ApiDate,
  separator = '-',
  withTime = false,
): string => {
  if (!value) return '';
  const parsed = readCalendarDate(value);
  if (!parsed.isValid()) return '';
  const date = parsed.format(['DD', 'MM', 'YYYY'].join(separator));
  return withTime ? `${date} ${parsed.format('HH:mm')}` : date;
};
